export interface PortalQuote {
  available: boolean
  reason: string | null
  invoice_amount_usdt: string
  payment_amount_rub: string | null
  min_rub: string | null
  max_rub: string | null
  rate_rub?: string | null
  min_usdt?: string | null
  kyc_required: boolean
}

export interface PortalTopupRequest {
  amount_usdt: string
  client_request_id: string
}

export type PortalTopupResponse = {
  attempt_id: string
  invoice_uuid: string
} & (
  | { result: 'setup_pending' }
  | {
      result: 'redirect'
      pay_url: string
      invoice_amount_usdt: string
      payment_amount_rub: string
    }
)

export const PORTAL_STATUSES = [
  'preparing',
  'kyc_required',
  'awaiting_payment',
  'processing',
  'completed',
  'failed',
  'manual_review',
] as const

export type PortalStatusName = (typeof PORTAL_STATUSES)[number]

export interface PortalStatus {
  /** Absent together for legacy USDT responses; never infer credit from provider settlement. */
  invoice_currency?: string
  invoice_amount?: string
  credited_amount?: string | null
  attempt_id: string
  invoice_uuid: string
  status: PortalStatusName
  invoice_amount_usdt: string
  payment_amount_rub: string | null
  credited_usdt: string | null
  pay_url: string | null
  error_code: string | null
}

/** Server recovery summary; payment status is not evidence of flow activation. */
export interface PortalFlowItem extends Omit<PortalStatus, 'status'> {
  status: PortalFlowStatusName
  types: string[]
  /** Original estimate, not a guaranteed exchange output or current basket price. */
  gnk_amount: string
  /** Backend-confirmed output; zero only for exchanged/wallet_zero_allocation. Never substitute the estimate. */
  actual_gnk_amount?: string
  client_request_id: string
  created_at: number
  progress_reason?: string | null
  purchase_complete?: boolean
  flows?: {
    type: string
    status: PortalFlowResultStatus
    /** Absent together with price_gnk for historical results. */
    purchase_status?: 'purchased' | 'insufficient' | 'not_attempted'
    price_gnk?: string | null
  }[]
}

export const PORTAL_FLOW_STATUSES = [
  'preparing',
  'kyc_required',
  'awaiting_payment',
  'processing',
  'payment_confirmed',
  'awaiting_liquidity',
  'converting',
  'exchanged',
  'partially_purchased',
  'prepaid',
  'activating',
  'activated',
  'failed',
  'manual_review',
] as const

export type PortalFlowStatusName = (typeof PORTAL_FLOW_STATUSES)[number]

export const PORTAL_FLOW_RESULT_STATUSES = [
  'prepaid',
  'activating',
  'activated',
  'manual_review',
] as const

export const PORTAL_PURCHASE_RESULT_STATUSES = [
  ...PORTAL_FLOW_RESULT_STATUSES,
  'insufficient',
  'not_attempted',
] as const

export type PortalFlowResultStatus =
  (typeof PORTAL_PURCHASE_RESULT_STATUSES)[number]

export interface PortalFlowPage {
  items: PortalFlowItem[]
  next_cursor: string | null
  flow_available?: boolean
}

export interface PortalFlowQuote {
  quote_id: string
  expires_at: number
  snapshot: {
    types: string[]
    items: { type: string; gnk_amount: string }[]
    gnk_amount: string
    gnk_target_minor: string
    swap_usdt_minor: string
    invoice_usdt_minor: string
    usdt_amount: string
    amount_rub_minor: string
    rub_amount: string
    rate_rub: string
    min_rub: string
    max_rub: string
    rounding_surplus_usdt_minor: string
    fees: { code: string; currency: 'USDT'; amount: string }[]
    gas_estimate: string
    swap_quote_source: 'gonka-rate-service' | 'uniswap-v3-quoter'
    swap_pool_fee: string
    kyc_required: boolean
  }
}

export interface PortalFlowCreateRequest {
  quote_id: string
  client_request_id: string
}
